import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Trophy, 
  Calendar, 
  MapPin, 
  Users, 
  Star, 
  ArrowLeft,
  Clock,
  CheckCircle,
  Camera,
  Wifi,
  Building2,
  AlertCircle,
  ChevronRight
} from 'lucide-react';
import { tournamentService } from '../services/tournamentService';
import type { Tournament, TournamentStage, TournamentEntry } from '@/core/types/tournament';
import { useAuth } from '@/modules/auth/AuthContext';

const StageDetailPage: React.FC = () => {
  const { stageId } = useParams<{ stageId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [stage, setStage] = useState<TournamentStage | null>(null);
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [entries, setEntries] = useState<TournamentEntry[]>([]);
  const [isRegistered, setIsRegistered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    if (stageId) {
      loadStageData(stageId);
    }
  }, [stageId]);

  useEffect(() => {
    if (stageId && user) {
      checkRegistration();
    }
  }, [stageId, user]);

  const loadStageData = async (id: string) => {
    try {
      const stageData = await tournamentService.getStageById(id);
      if (stageData) {
        setStage(stageData);
        
        // Carrega torneio pai
        const tournamentData = await tournamentService.getTournamentById(stageData.tournamentId);
        setTournament(tournamentData);
        
        // Carrega inscrições
        const entriesData = await tournamentService.getEntriesByStage(id);
        setEntries(entriesData);
      }
    } catch (error) {
      console.error('Erro ao carregar etapa:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkRegistration = async () => {
    if (!stageId || !user) return;
    const registered = await tournamentService.isAthleteRegistered(stageId, user.uid);
    setIsRegistered(registered);
  };

  const handleRegister = async () => {
    if (!user) {
      navigate('/login?redirect=' + encodeURIComponent(window.location.pathname));
      return;
    }

    if (!stage || !tournament) return;

    setRegistering(true);
    try {
      await tournamentService.registerAthlete(
        tournament.id,
        stage.id,
        user.uid
      );
      setIsRegistered(true);
      // Recarrega inscrições
      const entriesData = await tournamentService.getEntriesByStage(stage.id);
      setEntries(entriesData);
    } catch (error) {
      console.error('Erro ao inscrever:', error);
      alert('Erro ao realizar inscrição. Tente novamente.');
    } finally {
      setRegistering(false);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  const formatShortDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'short',
    }).format(date);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!stage || !tournament) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center text-white">
        <Calendar className="w-16 h-16 text-gray-600 mb-4" />
        <h2 className="text-2xl font-bold mb-2">Etapa não encontrada</h2>
        <Link to="/torneios" className="text-amber-400 hover:underline">
          Voltar para lista de torneios
        </Link>
      </div>
    );
  }

  const now = new Date();
  const registrationOpen = stage.dates.registrationDeadline > now;
  const eventStarted = stage.dates.start <= now;
  const eventEnded = stage.dates.end < now;

  return (
    <div className="min-h-screen bg-gray-900 text-white pb-24">
      {/* Header */}
      <div className="relative">
        {/* Banner Gradient */}
        <div className="h-48 bg-gradient-to-br from-purple-600 via-amber-600 to-orange-600" />

        {/* Back Button */}
        <Link
          to={`/torneios/${tournament.id}`}
          className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm rounded-full p-2 hover:bg-black/70 transition-colors flex items-center gap-2"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm hidden sm:inline">Voltar ao Torneio</span>
        </Link>

        {/* Stage Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900 via-gray-900/90 to-transparent p-6">
          <div className="max-w-4xl mx-auto">
            {/* Tournament Badge */}
            <Link 
              to={`/torneios/${tournament.id}`}
              className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 mb-2"
            >
              <Trophy className="w-3 h-3" />
              {tournament.name}
              <ChevronRight className="w-3 h-3" />
            </Link>
            
            <div className="flex items-center gap-2 mb-2">
              {stage.isVirtual && (
                <span className="bg-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                  <Wifi className="w-3 h-3" />
                  VIRTUAL
                </span>
              )}
              {tournament.type === 'official' && (
                <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  OFICIAL CBPE
                </span>
              )}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold">{stage.name}</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        
        {/* Status Card */}
        <div className={`rounded-xl p-4 flex items-center gap-4 ${
          eventEnded 
            ? 'bg-gray-800' 
            : eventStarted 
              ? 'bg-green-900/30 border border-green-500/30' 
              : registrationOpen 
                ? 'bg-amber-900/30 border border-amber-500/30'
                : 'bg-red-900/30 border border-red-500/30'
        }`}>
          {eventEnded ? (
            <>
              <Clock className="w-8 h-8 text-gray-500" />
              <div>
                <p className="font-bold text-gray-400">Evento Encerrado</p>
                <p className="text-sm text-gray-500">Esta etapa já foi realizada</p>
              </div>
            </>
          ) : eventStarted ? (
            <>
              <CheckCircle className="w-8 h-8 text-green-400" />
              <div>
                <p className="font-bold text-green-400">🎣 Em Andamento!</p>
                <p className="text-sm text-green-300">A competição está acontecendo agora</p>
              </div>
            </>
          ) : registrationOpen ? (
            <>
              <AlertCircle className="w-8 h-8 text-amber-400" />
              <div>
                <p className="font-bold text-amber-400">Inscrições Abertas</p>
                <p className="text-sm text-amber-300">Até {formatShortDate(stage.dates.registrationDeadline)}</p>
              </div>
            </>
          ) : (
            <>
              <Clock className="w-8 h-8 text-red-400" />
              <div>
                <p className="font-bold text-red-400">Inscrições Encerradas</p>
                <p className="text-sm text-red-300">Aguarde o início do evento</p>
              </div>
            </>
          )}
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Location */}
          <div className="bg-gray-800 rounded-xl p-5">
            <h3 className="flex items-center gap-2 text-lg font-bold mb-3">
              <MapPin className="w-5 h-5 text-amber-400" />
              Local
            </h3>
            <p className="text-white font-medium">{stage.location.name}</p>
            <p className="text-gray-400">{stage.location.city}, {stage.location.state}</p>
            
            {stage.location.coordinates && (
              <a
                href={`https://maps.google.com/?q=${stage.location.coordinates.lat},${stage.location.coordinates.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-amber-400 hover:underline mt-3"
              >
                Ver no Google Maps
                <ChevronRight className="w-4 h-4" />
              </a>
            )}
          </div>

          {/* Dates */}
          <div className="bg-gray-800 rounded-xl p-5">
            <h3 className="flex items-center gap-2 text-lg font-bold mb-3">
              <Calendar className="w-5 h-5 text-amber-400" />
              Datas
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Início:</span>
                <span className="text-white">{formatDate(stage.dates.start)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Término:</span>
                <span className="text-white">{formatDate(stage.dates.end)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Limite Inscrição:</span>
                <span className="text-amber-400">{formatDate(stage.dates.registrationDeadline)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Virtual Badge Info */}
        {stage.isVirtual && (
          <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-5">
            <h3 className="flex items-center gap-2 text-lg font-bold mb-2">
              <Camera className="w-5 h-5 text-purple-400" />
              Torneio Virtual - Digital Livewell
            </h3>
            <p className="text-gray-300 text-sm mb-3">
              Esta etapa utiliza nosso sistema de validação digital. Capture suas fotos 
              pelo app e envie para validação automática por IA.
            </p>
            {stage.geofenceRadius && (
              <p className="text-purple-300 text-sm">
                📍 Raio permitido: <strong>{stage.geofenceRadius} km</strong> do ponto central
              </p>
            )}
          </div>
        )}

        {/* Official Lodging */}
        {stage.officialLodging && stage.officialLodging.length > 0 && (
          <div className="bg-gray-800 rounded-xl p-5">
            <h3 className="flex items-center gap-2 text-lg font-bold mb-3">
              <Building2 className="w-5 h-5 text-amber-400" />
              Hospedagem Oficial
            </h3>
            <p className="text-gray-400 text-sm mb-3">
              Confira as pousadas parceiras com condições especiais para competidores.
            </p>
            <div className="flex flex-wrap gap-2">
              {stage.officialLodging.map((tenantId) => (
                <Link
                  key={tenantId}
                  to={`/empresas/${tenantId}`}
                  className="bg-amber-500/10 text-amber-400 text-sm px-3 py-1 rounded-full hover:bg-amber-500/20 transition-colors"
                >
                  Ver Pousada →
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Participants */}
        <div className="bg-gray-800 rounded-xl p-5">
          <h3 className="flex items-center gap-2 text-lg font-bold mb-3">
            <Users className="w-5 h-5 text-amber-400" />
            Participantes Confirmados ({entries.length})
          </h3>
          
          {entries.length === 0 ? (
            <p className="text-gray-500 text-sm">Nenhuma inscrição confirmada ainda.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {entries.slice(0, 20).map((entry, i) => (
                <div 
                  key={entry.id}
                  className="bg-gray-700 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                >
                  <span className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-xs font-bold text-black">
                    {i + 1}
                  </span>
                  <span className="text-gray-300">Atleta #{entry.athleteId.slice(-4)}</span>
                </div>
              ))}
              {entries.length > 20 && (
                <span className="text-gray-500 text-sm self-center">
                  +{entries.length - 20} outros
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Fixed CTA */}
      {!eventEnded && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur border-t border-gray-800 p-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-gray-400">
                {entries.length} inscritos
              </p>
              <p className="font-bold text-white">
                {isRegistered ? '✅ Você está inscrito!' : 'Garanta sua vaga'}
              </p>
            </div>
            
            {isRegistered ? (
              <button
                disabled
                className="bg-green-600 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Inscrito
              </button>
            ) : registrationOpen ? (
              <button
                onClick={handleRegister}
                disabled={registering}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-black px-6 py-3 rounded-xl font-bold hover:from-amber-400 hover:to-orange-400 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {registering ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Inscrevendo...
                  </>
                ) : (
                  <>
                    <Trophy className="w-5 h-5" />
                    Inscrever-se
                  </>
                )}
              </button>
            ) : (
              <button
                disabled
                className="bg-gray-700 text-gray-400 px-6 py-3 rounded-xl font-bold cursor-not-allowed"
              >
                Inscrições Encerradas
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StageDetailPage;
